import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { get } from "lodash";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableService } from "../../../shared/components/data-table/data-table.service";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { yesNoOptions } from "../../../shared/constants/components";
import { pattern } from "../../../shared/constants/patterns";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { getInternalProviderSearchFilterOptions } from "./internal-provider";
import { InternalProviderService } from "./internal-provider.service";
import { InternalProvider } from "./internal-provider.type";

@Injectable({
  providedIn: "root",
})
export class InternalProviderTableService {
  currentUser: UserInfo;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private internalProviderService: InternalProviderService,
    private dataTableService: DataTableService<InternalProvider>,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  /** Get table config */
  getInternalProviderTableConfig = (config?: DataTableConfig): DataTable<InternalProvider> => {
    const { hidden, shown, actionColumn } = config || {};

    const columns: DataTableColumn<InternalProvider>[] = (
      [
        {
          prop: "name",
          name: "Name",
          link: {
            path: (internalProvider) => `/internal-providers/${internalProvider.id}`,
          },
          inlineEdit: {
            control: "input",
            validators: () => [Validators.minLength(1), Validators.maxLength(255), Validators.pattern(pattern.words)],
          },
        },
        {
          prop: "email",
          name: "Email",
          inlineEdit: {
            control: "input",
            required: false,
            validators: () => [Validators.maxLength(255), Validators.pattern(pattern.email)],
          },
        },
        {
          prop: "phone",
          name: "Phone",
          inlineEdit: {
            control: "input",
            required: false,
            validators: () => [Validators.maxLength(255)],
          },
        },
        {
          prop: "enabled",
          name: "Enabled",
          pipes: [{ clazz: BoolToStringPipe }],
          inlineEdit: {
            control: "select",
            select: {
              options: [...yesNoOptions],
            },
          },
        },
      ] as DataTableColumn<InternalProvider>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (internalProvider) => col?.inlineEdit?.disabled?.(internalProvider),
        },
      }));

    return {
      id: "ip",
      entityName: "Internal Provider",
      syncWithQueryParams: true,
      toolBar: {
        exportCsv: {
          filename: "internal-providers",
          data: {
            browser: (queryParams) =>
              this.internalProviderService.getInternalProviders({
                ...queryParams,
                where: {
                  ...queryParams?.where,
                },
              }),
            email: (queryParams) =>
              this.internalProviderService.getInternalProvidersQueue({
                ...queryParams,
                where: {
                  ...queryParams?.where,
                },
              }),
          },
        },
      },
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: (company) => this.viewInternalProvider(company, config),
              hidden: actionColumn?.quickView?.hidden,
            },
            {
              icon: "visibility",
              text: "View",
              function: this.manageInternalProvider,
              hidden: (internalProvider) => actionColumn?.view?.hidden,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteInternalProvider,
              hidden: (internalProvider) => actionColumn?.delete?.hidden || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getInternalProviderSearchFilterOptions(this.currentUser),
        },
      ],
    };
  };

  /** Populate table */
  getInternalProviders = (event: GetDataEvent<InternalProvider>): void => {
    const { onError, onResult, queryParams } = event;
    this.loader.show();
    this.internalProviderService
      .getInternalProviders({
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data) => {
          onResult(data);
          this.loader.hide();
        },
        (error) => {
          onError(error);
          this.loader.hide();
        },
      );
  };

  /** Update table */
  update = (event: UpdateDataEvent<InternalProvider>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.internalProviderService.updateInternalProvider(row.id, { ...updatedValues }).subscribe(
        (data): void => {
          onResult(data);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  //#region Private Methods

  private viewInternalProvider = (internalProvider: InternalProvider, config?: DataTableConfig): void => {
    const columns = this.getInternalProviderTableConfig(config).columns;

    const data = columns
      .filter((col) => !col.hidden)
      .map((col) => ({
        name: col.name,
        value: this.dataTableService.parseData(internalProvider, col, get(internalProvider, col.prop)),
      }));

    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private manageInternalProvider = (internalProvider: InternalProvider): void => {
    this.router.navigate([`/internal-providers/${internalProvider.id}`]);
  };

  private deleteInternalProvider = (
    internalProvider: InternalProvider,
    table: DataTableComponent<InternalProvider>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "internalProvider",
        data: internalProvider,
        url: this.internalProviderService.basePath,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  //#endregion
}
